// utils/navigationGroups.ts

export type RouteLink = {
    id: number
    text: string
    to: string
    icon: string
    subText?: string
    disabled?: boolean
    menu?: boolean
    subItems?: RouteLink[]
    width?: string
}


export type NavDrawerGroup = {
    header: string
    items: RouteLink[]
}

interface AppBarTabsGroup {
    id: number
    text: string
    to: string
    icon?: string // for MDI in secure app bar you pass full "$mdiX" strings
    style?: string
}


/* ─────────────────────────────────────────────────────────────
   SECURE (AUTHED) NAV
   ───────────────────────────────────────────────────────────── */

export const secureDrawerRoutes: NavDrawerGroup[] = [
    {
        header: 'Members Area',
        items: [

            { id: 101, text: 'Dashboard', to: '/dashboard', icon: '$mdiViewDashboard' },
            { id: 102, text: 'Videos', to: '/videos', icon: '$mdiPlayCircleOutline' },
            // { id: 103, text: 'Apparel', to: '/store', icon: '$mdiTshirtCrewOutline' },
        ],
    },
    {
        header: 'Account',
        items: [
            { id: 201, text: 'Profile', to: '/account', icon: '$mdiAccountCircle' },
            { id: 202, text: 'Orders', to: '/orders', icon: '$mdiReceiptTextOutline' },
            // { id: 203, text: 'Support Tickets', to: '/tickets', icon: '$mdiLifebuoy' },
        ],
    },
]


export const secureAppBarRoutes: AppBarTabsGroup[] = [
    { id: 1, text: 'Dashboard', to: '/dashboard', icon: '$mdiViewDashboard', style: '' },
    { id: 2, text: 'Videos', to: '/videos', icon: '$mdiPlayCircleOutline', style: '' },
    // { id: 3, text: 'Apparel', to: '/store', icon: '$mdiTshirtCrewOutline', style: '' },
    { id: 4, text: 'Orders', to: '/orders', icon: '$mdiReceiptTextOutline', style: '' },
    // { id: 5, text: 'Support', to: '/tickets', icon: '$mdiLifebuoy', style: '' },
]

/* Optional: secure footer nav (if you still use these) */

export const secureGeneralFooterRoutes: RouteLink[] = [
    { id: 401, text: 'Dashboard', to: '/dashboard', icon: 'ViewDashboard' },
    { id: 402, text: 'Courses', to: '/courses', icon: 'School' },
    { id: 403, text: 'Videos', to: '/videos', icon: 'PlayCircle' },

    { id: 405, text: 'Apparel', to: '/store', icon: 'TshirtCrew' },

    // ✅ NEW: Orders
    { id: 406, text: 'Orders', to: '/orders', icon: 'ReceiptText' },

    { id: 404, text: 'Logout', to: '/logout', icon: 'Logout' },
]

export const secureServerFooterRoutes: RouteLink[] = [
    // repurposed: “Training” quick links
    { id: 411, text: 'Courses', to: '/courses', icon: 'School' },
    { id: 412, text: 'Videos', to: '/videos', icon: 'PlayCircle' },
    { id: 413, text: 'Resources', to: '/resources', icon: 'Folder' },

    // ✅ Support -> Tickets
    // { id: 414, text: 'Support', to: '/tickets', icon: 'Lifebuoy' },
]

export const secureBillingFooterRoutes: RouteLink[] = [
    // repurposed: “Account” quick links
    { id: 421, text: 'Profile', to: '/account', icon: 'Account' },
    { id: 422, text: 'Settings', to: '/settings', icon: 'Cog' },
]

/* ─────────────────────────────────────────────────────────────
   PUBLIC (UNAUTHED) NAV
   ───────────────────────────────────────────────────────────── */

export const generalRoutes: RouteLink[] = [
    { id: 1, text: 'Home', to: '/', icon: 'Home' },
    {
        id: 2,
        text: 'Results',
        to: '/results',
        icon: 'ChartLine',
        menu: true,
        subItems: [
            {
                id: 21,
                text: 'Transformations',
                to: '/results/transformations',
                icon: 'ImageMultiple',
                subText: 'Before & afters and what changed.',
                width: '100',
            },
            {
                id: 22,
                text: 'Testimonials',
                to: '/results/testimonials',
                icon: 'MessageText',
                subText: 'Client feedback and reviews.',
                width: '100',
            },
        ],
    },
    { id: 3, text: 'FAQs', to: '/faqs', icon: 'FrequentlyAskedQuestions' },
    { id: 4, text: 'Contact', to: '/contact', icon: 'Email' },
]

export const authRoutes: RouteLink[] = [
    { id: 13, text: 'Login', to: '/auth/login', icon: 'Login' },
    // Most coaching sites don’t want open registration - enable if you do.
    // { id: 14, text: 'Register', to: '/auth/register', icon: 'AccountPlus' },
    { id: 15, text: 'Forgot Password', to: '/auth/forgot', icon: 'LockQuestion' },
]

export const supportRoutes: RouteLink[] = [
    { id: 16, text: 'Contact', to: '/contact', icon: 'Email' },

    // Public support page (optional). If you don’t have /support public, delete this.
    // If you DO want a public Support page, keep this and on that page
    // link authed users to /tickets and unauth users to /auth/login.
    { id: 17, text: 'Support', to: '/support', icon: 'Lifebuoy' },
]

export const legalRoutes: RouteLink[] = [
    { id: 18, text: 'Terms', to: '/terms', icon: 'ScaleBalance' },
    { id: 19, text: 'Privacy', to: '/privacy', icon: 'Lock' },
]

/* ─────────────────────────────────────────────────────────────
   ADMIN NAV (COACH/CLIENT MANAGEMENT + CONTENT)
   ───────────────────────────────────────────────────────────── */

export const adminRoutes: NavDrawerGroup[] = [
    {
        header: 'Overview',
        items: [
            {
                id: 500,
                text: 'Dashboard',
                to: '/admin',
                icon: '$mdiShieldCrownOutline',
                subText: 'Quick visibility of key operational metrics and recent activity.',
            },
        ],
    },

    {
        header: 'Operations',
        items: [
            {
                id: 501,
                text: 'Users',
                to: '/admin/users',
                icon: '$mdiAccountGroup',
                subText: 'Manage accounts, roles, suspensions, and general customer state.',
            },
            {
                id: 502,
                text: 'Tickets',
                to: '/admin/tickets',
                icon: '$mdiTicketConfirmationOutline',
                subText: 'Handle support requests, triage, and resolution workflows.',
            },
        ],
    },

    {
        header: 'Shop',
        items: [
            {
                id: 540,
                text: 'Apparel',
                to: '/admin/store',
                icon: '$mdiTshirtCrewOutline',
                subText: 'Manage products, variants, stock, and order fulfilment tooling.',
            },
        ],
    },

    {
        header: 'Video Management',
        items: [
            {
                id: 509,
                text: 'Modules',
                to: '/admin/modules',
                icon: '$mdiViewModuleOutline',
                subText: 'Create and organise modules (video categories / programme structure).',
            },
            {
                id: 510,
                text: 'Videos',
                to: '/admin/videos',
                icon: '$mdiPlayBoxMultipleOutline',
                subText: 'Create videos, assign to modules, order them, and publish drafts.',
            },
        ],
    },

    {
        header: 'Site Content',
        items: [
            {
                id: 522,
                text: 'FAQs',
                to: '/admin/faqs',
                icon: '$mdiFrequentlyAskedQuestions',
                subText: 'Maintain FAQs that reduce support load and improve conversion.',
            },
            {
                id: 520,
                text: 'Testimonials',
                to: '/admin/testimonials',
                icon: '$mdiMessageTextOutline',
                subText: 'Manage customer reviews and featured quotes for landing pages.',
            },
            {
                id: 521,
                text: 'Transformations',
                to: '/admin/transformations',
                icon: '$mdiImageMultipleOutline',
                subText: 'Before/after assets and showcase entries.',
            },
        ],
    }
]

/* ─────────────────────────────────────────────────────────────
   Export
   ───────────────────────────────────────────────────────────── */

export default {
    secureDrawerRoutes,
    secureAppBarRoutes,
    generalRoutes,
    secureGeneralFooterRoutes,
    secureServerFooterRoutes,
    secureBillingFooterRoutes,
    authRoutes,
    supportRoutes,
    legalRoutes,
    adminRoutes,
}
